import React from 'react';
import styled from 'styled-components';
import { Zap, ExternalLink, Check, ChevronRight, Sparkles, Plus, Clock, Award } from 'lucide-react';
import { SpaceInfo } from '../../types';
import { SPACE_TYPE_COLORS, SpaceType } from '../../constants';
import { 
  Section, SectionHeader, SectionTitleWrapper, SectionIcon, 
  SectionTitle, SectionSubtitle, SectionBadge, EmptyActiveSpaces, 
  EmptyIconWrapper, EmptyTitle, EmptyDescription, fadeIn, spin 
} from '../../SharedStyles';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';

interface SpaceTabProps {
  activeSpaces: SpaceInfo[];
  availableSpaces: SpaceInfo[];
  companyName: string;
  handleAccessSpace: (spaceType: string) => void;
  handleCreateSpace: (spaceType: string) => void;
  isLoading: boolean;
}

const SpaceTab: React.FC<SpaceTabProps> = ({
  activeSpaces,
  availableSpaces,
  companyName,
  handleAccessSpace,
  handleCreateSpace,
  isLoading
}) => {
  return (
    <>
      {/* 활성 스페이스 섹션 */}
      <Section>
        <SectionHeader>
          <SectionTitleWrapper>
            <SectionIcon $variant="success">
              <Zap size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>활성 스페이스</SectionTitle>
              <SectionSubtitle>현재 사용 중인 서비스입니다</SectionSubtitle>
            </div>
          </SectionTitleWrapper>
          {activeSpaces.length > 0 && (
            <SectionBadge>{activeSpaces.length}개</SectionBadge>
          )}
        </SectionHeader>
        
        {activeSpaces.length > 0 ? (
          <ActiveSpaceGrid>
            {activeSpaces.map((space, index) => (
              <ActiveSpaceCard 
                key={space.type}
                $delay={index * 0.1}
                $type={space.type as SpaceType}
                onClick={() => handleAccessSpace(space.type)}
              >
                <ActiveSpaceTop>
                  <ActiveSpaceIconWrapper $type={space.type as SpaceType}>
                    {space.icon}
                  </ActiveSpaceIconWrapper>
                <ActiveSpaceInfo>
                  <ActiveSpaceName>{space.name}</ActiveSpaceName>
                  <ActiveSpaceDesc>
                    {space.isPreparing ? '서비스 준비 중입니다.' : space.description}
                  </ActiveSpaceDesc>
                </ActiveSpaceInfo>
                  <ActiveSpaceArrow>
                    <ExternalLink size={20} />
                  </ActiveSpaceArrow>
                </ActiveSpaceTop>
                <ActiveSpaceFeatures>
                  {getSpaceFeatures(space.type).slice(0, 3).map((feature, idx) => (
                    <ActiveFeatureTag key={idx} $type={space.type as SpaceType}>
                      <Check size={12} />
                      {feature}
                    </ActiveFeatureTag>
                  ))}
                </ActiveSpaceFeatures>
                <AccessButton $type={space.type as SpaceType}>
                  <span>스페이스 접속</span>
                  <ChevronRight size={16} />
                </AccessButton>
              </ActiveSpaceCard>
            ))}
          </ActiveSpaceGrid>
        ) : (
          <EmptyActiveSpaces>
            <EmptyIconWrapper>
              <Sparkles size={48} />
            </EmptyIconWrapper>
            <EmptyTitle>활성화된 스페이스가 없습니다</EmptyTitle>
            <EmptyDescription>
              아래에서 원하는 스페이스를 추가하고 시작해보세요!
            </EmptyDescription>
          </EmptyActiveSpaces>
        )}
      </Section>

      {/* 추가 가능한 스페이스 섹션 */}
      {availableSpaces.length > 0 && (
        <Section>
          <SectionHeader>
            <SectionTitleWrapper>
              <SectionIcon $variant="primary">
                <Plus size={20} />
              </SectionIcon>
              <div>
                <SectionTitle>추가 가능한 스페이스</SectionTitle>
                <SectionSubtitle>비즈니스 성장에 필요한 서비스를 추가하세요</SectionSubtitle>
              </div>
            </SectionTitleWrapper>
          </SectionHeader>
          
          <AvailableSpaceGrid>
            {availableSpaces.map((space, index) => (
              <AvailableSpaceCard key={space.type} $delay={index * 0.1}>
                <AvailableSpaceHeader>
                  <AvailableSpaceIconWrapper $type={space.type as SpaceType}>
                    {space.icon}
                  </AvailableSpaceIconWrapper>
                  <AvailableSpaceTitle>
                    <AvailableSpaceName>{space.name}</AvailableSpaceName>
                    <AvailableSpaceTag $type={space.type as SpaceType}>
                      {space.isPreparing ? '준비 중' : '추가 가능'}
                    </AvailableSpaceTag>
                  </AvailableSpaceTitle>
                </AvailableSpaceHeader>

                <AvailableSpaceDesc>
                  {space.isPreparing ? '현재 서비스 준비 중인 상품입니다. 빠른 시일 내에 만나보실 수 있도록 노력하겠습니다.' : getServiceDetailDescription(space.type)}
                </AvailableSpaceDesc>

                <AvailableSpaceFeatures>
                  <FeaturesTitle>주요 기능</FeaturesTitle>
                  <FeaturesList>
                    {getSpaceFeatures(space.type).map((feature, idx) => (
                      <FeatureItem key={idx}>
                        <FeatureIcon $type={space.type as SpaceType}>
                          <Check size={14} />
                        </FeatureIcon>
                      <FeatureText>{feature}</FeatureText>
                    </FeatureItem>
                  ))}
                  </FeaturesList>
                </AvailableSpaceFeatures>
                
                <AddSpaceButton
                  $type={space.type as SpaceType}
                  onClick={() => handleCreateSpace(space.type)}
                  disabled={isLoading || space.isPreparing}
                >
                  {isLoading ? (
                    <>
                      <ButtonSpinner />
                      처리 중...
                    </>
                  ) : space.isPreparing ? (
                    <>
                      <Clock size={18} />
                      준비 중입니다
                    </>
                  ) : (
                    <>
                      <Plus size={18} />
                      스페이스 추가하기
                    </>
                  )}
                </AddSpaceButton>
              </AvailableSpaceCard>
            ))}
          </AvailableSpaceGrid>
        </Section>
      )}

      {/* 모든 서비스 이용 중 */}
      {availableSpaces.length === 0 && activeSpaces.length > 0 && (
        <AllServicesCard>
          <AllServicesIcon>
            <Award size={48} />
          </AllServicesIcon>
          <AllServicesTitle>모든 스페이스를 이용 중입니다! 🎉</AllServicesTitle>
          <AllServicesDesc>
            {companyName}에서 제공하는 모든 스페이스를 사용하고 계십니다.
            <br />
            새로운 서비스가 출시되면 알려드리겠습니다.
          </AllServicesDesc>
        </AllServicesCard>
      )}
    </>
  );
};

/**
 * 공간 타입별 주요 기능 반환
 */
const getSpaceFeatures = (spaceType: string): string[] => {
  switch (spaceType) {
    case 'STARTUP':
      return [
        '회사 정보 관리',
        '투자 유치 현황',
        '사업 계획서 관리',
        '투자자 연결',
      ];
    case 'INVESTOR':
      return [
        '포트폴리오 관리',
        '딜 소싱',
        '투자 분석',
        '리포트 생성',
      ];
    case 'INNOVATE':
      return [
        '프로그램 운영',
        '참가자 관리',
        '평가 시스템',
        '성과 분석',
      ];
    default:
      return [];
  }
};

/**
 * 서비스별 상세 설명 반환
 */
const getServiceDetailDescription = (spaceType: string): string => {
  switch (spaceType) {
    case 'STARTUP':
      return '스타트업의 성장 단계별 맞춤형 솔루션을 제공합니다. 투자 유치부터 사업 확장까지 필요한 모든 도구와 네트워크를 한 곳에서 만나보세요.';
    case 'INVESTOR':
      return '전문적인 투자 관리 플랫폼으로 딜 소싱, 포트폴리오 추적, 실사 관리까지 투자 업무의 모든 과정을 효율적으로 관리할 수 있습니다.';
    case 'INNOVATE':
      return '혁신 프로그램을 체계적으로 운영할 수 있는 통합 플랫폼입니다. 참가자 선발부터 성과 측정까지 프로그램의 전 과정을 디지털화하세요.';
    default:
      return '';
  }
};

const ActiveSpaceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.25rem;
`;

const ActiveSpaceCard = styled.div<{ $delay: number; $type: SpaceType }>`
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background: ${semanticColors.background.secondary};
  border: 2px solid ${semanticColors.border.primary};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.25s ease;
  animation: ${fadeIn} 0.4s ease-out ${props => props.$delay}s both;
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    border-radius: 20px 20px 0 0;
    background: ${props => SPACE_TYPE_COLORS[props.$type]?.gradient || semanticColors.background.brand};
    opacity: 0;
    transition: opacity 0.25s ease;
  }
  
  &:hover {
    border-color: ${props => SPACE_TYPE_COLORS[props.$type]?.color || semanticColors.border.brand};
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0,0,0,0.1);
    
    &::before {
      opacity: 1;
    }
  }
`;

const ActiveSpaceTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const ActiveSpaceIconWrapper = styled.div<{ $type: SpaceType }>`
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  flex-shrink: 0;
  transition: all 0.25s ease;
  
  background: ${props => SPACE_TYPE_COLORS[props.$type]?.bgMedium || 'transparent'};
  color: ${props => SPACE_TYPE_COLORS[props.$type]?.color || 'inherit'};
  
  ${ActiveSpaceCard}:hover & {
    transform: scale(1.05);
  }
`;

const ActiveSpaceInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActiveSpaceName = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.25rem;
`;

const ActiveSpaceDesc = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const ActiveSpaceArrow = styled.div`
  color: ${semanticColors.text.tertiary};
  transition: all 0.25s ease;
  
  ${ActiveSpaceCard}:hover & {
    color: ${semanticColors.text.brand};
    transform: translateX(4px);
  }
`;

const ActiveSpaceFeatures = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
`;

const ActiveFeatureTag = styled.span<{ $type: SpaceType }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  border-radius: 100px;
  ${typography.caption.caption2}
  
  background: ${props => SPACE_TYPE_COLORS[props.$type]?.bgLight || 'transparent'};
  color: ${props => SPACE_TYPE_COLORS[props.$type]?.color || 'inherit'};
`;

const AccessButton = styled.div<{ $type: SpaceType }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 10px;
  ${typography.paragraph.p2.default}
  font-weight: 600;
  transition: all 0.2s ease;
  
  background: ${props => SPACE_TYPE_COLORS[props.$type]?.bgLight || 'transparent'};
  color: ${props => SPACE_TYPE_COLORS[props.$type]?.color || 'inherit'};
  
  ${ActiveSpaceCard}:hover & {
    background: ${props => SPACE_TYPE_COLORS[props.$type]?.gradient || 'transparent'};
    color: #fff;
  }
`;

const AvailableSpaceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.25rem;
`;

const AvailableSpaceCard = styled.div<{ $delay: number }>`
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 20px;
  transition: all 0.25s ease;
  animation: ${fadeIn} 0.4s ease-out ${props => props.$delay}s both;
  
  &:hover {
    border-color: ${semanticColors.border.secondary};
    box-shadow: 0 8px 24px rgba(0,0,0,0.06);
  }
`;

const AvailableSpaceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const AvailableSpaceIconWrapper = styled.div<{ $type: SpaceType }>`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  flex-shrink: 0;
  
  background: ${props => SPACE_TYPE_COLORS[props.$type]?.bgMedium || 'transparent'};
  color: ${props => SPACE_TYPE_COLORS[props.$type]?.color || 'inherit'};
`;

const AvailableSpaceTitle = styled.div`
  flex: 1;
`;

const AvailableSpaceName = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.25rem;
`;

const AvailableSpaceTag = styled.span<{ $type: SpaceType }>`
  display: inline-flex;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  ${typography.caption.caption2}
  
  background: ${props => SPACE_TYPE_COLORS[props.$type]?.bgLight || 'transparent'};
  color: ${props => SPACE_TYPE_COLORS[props.$type]?.color || 'inherit'};
`;

const AvailableSpaceDesc = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  line-height: 1.6;
  margin-bottom: 1.25rem;
`;

const AvailableSpaceFeatures = styled.div`
  flex: 1;
  margin-bottom: 1.25rem;
`;

const FeaturesTitle = styled.h4`
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.tertiary};
  margin-bottom: 0.75rem;
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FeatureIcon = styled.div<{ $type: SpaceType }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  background: ${props => SPACE_TYPE_COLORS[props.$type]?.bgLight || 'transparent'};
  color: ${props => SPACE_TYPE_COLORS[props.$type]?.color || 'inherit'};
`;

const FeatureText = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.secondary};
`;

const AddSpaceButton = styled.button<{ $type: SpaceType }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem;
  border: none;
  border-radius: 12px;
  ${typography.paragraph.p2.default}
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: all 0.25s ease;
  
  background: ${props => SPACE_TYPE_COLORS[props.$type]?.gradient || 'transparent'};
  
  &:hover:not(:disabled) {
    box-shadow: ${props => SPACE_TYPE_COLORS[props.$type]?.hoverShadow || 'none'};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ButtonSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const AllServicesCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 2rem;
  background: linear-gradient(135deg, ${semanticColors.background.success.secondary} 0%, rgba(16, 185, 129, 0.05) 100%);
  border: 1px solid ${semanticColors.border.success};
  border-radius: 24px;
  text-align: center;
  animation: ${fadeIn} 0.6s ease-out 0.2s both;
`;

const AllServicesIcon = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  border-radius: 50%;
  color: #fff;
  margin-bottom: 1.5rem;
`;

const AllServicesTitle = styled.h3`
  ${typography.heading.h4}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.75rem;
`;

const AllServicesDesc = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  line-height: 1.6;
`;

export default SpaceTab;
